import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGE_STYLES = {
  ANGLE: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
  SCRIPT: "bg-blue-500/10 text-blue-300 border-blue-500/30",
  BRIEF: "bg-violet-500/10 text-violet-300 border-violet-500/30",
  PROMPT: "bg-amber-500/10 text-amber-300 border-amber-500/30",
};

const TYPE_STYLES = {
  APPROVED: "text-emerald-400",
  REJECTED: "text-red-400",
  EDITED: "text-amber-300",
  CUSTOM_SUBMITTED: "text-blue-300",
};

const CreativeHistory = ({ creativeId: creativeIdProp } = {}) => {
  // `creativeId` may be passed in (v2 opens this in place, inside the session
  // that made the creative) or come from the route (v1's
  // `/creatives/:creativeId/history`). The prop wins; the param is the
  // fallback, so BOTH callers work and neither changes behaviour.
  const params = useParams();
  const creativeId = creativeIdProp || params.creativeId;
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!creativeId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/creatives/${creativeId}/feedback-log`);
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load feedback log");
    } finally {
      setLoading(false);
    }
  }, [creativeId]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="creative-history">
      <div className="max-w-4xl mx-auto px-6 py-8">
        <header className="mb-6 flex items-start justify-between gap-4">
          <div>
            <button
              onClick={() => nav(-1)}
              className="text-xs text-slate-500 hover:text-slate-300 mb-2"
              data-testid="creative-history-back"
            >
              ← Back
            </button>
            <h1 className="text-2xl font-semibold" data-testid="creative-history-title">
              Creative history
            </h1>
            <p className="text-sm text-slate-400 mt-1">
              {data?.creative_name || creativeId} · {data?.count ?? 0} events
            </p>
          </div>
          <button
            onClick={load}
            data-testid="creative-history-refresh"
            className="px-3 py-1.5 rounded-lg border border-slate-700 hover:border-slate-500 text-xs text-slate-300"
          >
            Refresh
          </button>
        </header>

        {error && (
          <div className="bg-red-950/40 border border-red-500/30 text-red-200 text-sm rounded-lg p-4 mb-4" data-testid="creative-history-error">
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-center text-slate-500 py-16" data-testid="creative-history-loading">Loading…</div>
        ) : (data?.entries || []).length === 0 ? (
          <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-12 text-center text-slate-400" data-testid="creative-history-empty">
            No feedback recorded for this creative yet.
          </div>
        ) : (
          <ol className="relative border-l border-slate-800 ml-2 space-y-4" data-testid="creative-history-timeline">
            {(data.entries).map((entry, i) => (
              <li key={i} className="ml-4" data-testid={`creative-history-entry-${i}`}>
                <span className="absolute -left-1.5 mt-1.5 h-3 w-3 rounded-full bg-slate-700 border-2 border-slate-950"></span>
                <div className="bg-slate-900/60 border border-slate-800 rounded-lg p-4">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${STAGE_STYLES[entry.stage] || ""}`}>
                      {entry.stage}
                    </span>
                    <span className={`text-xs font-medium ${TYPE_STYLES[entry.feedback_type] || "text-slate-300"}`}>
                      {entry.feedback_type}
                    </span>
                    <span className="text-[11px] text-slate-500 ml-auto">
                      {entry.timestamp ? new Date(entry.timestamp).toLocaleString() : ""}
                      {entry.operator_id && <span className="ml-2 text-slate-600">by {entry.operator_id}</span>}
                    </span>
                  </div>
                  {entry.feedback_text && (
                    <p className="text-sm text-slate-200 mb-2 whitespace-pre-wrap">{entry.feedback_text}</p>
                  )}
                  {(entry.original_output || entry.edited_output) && (
                    <details className="text-xs">
                      <summary className="cursor-pointer text-slate-500 hover:text-slate-300">View diff</summary>
                      <div className="mt-2 grid md:grid-cols-2 gap-2">
                        {entry.original_output && (
                          <div>
                            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Original</div>
                            <pre className="whitespace-pre-wrap font-mono text-[11px] bg-slate-950/60 border border-slate-800 rounded p-2 max-h-40 overflow-y-auto">{entry.original_output}</pre>
                          </div>
                        )}
                        {entry.edited_output && (
                          <div>
                            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Final</div>
                            <pre className="whitespace-pre-wrap font-mono text-[11px] bg-slate-950/60 border border-emerald-500/30 rounded p-2 max-h-40 overflow-y-auto">{entry.edited_output}</pre>
                          </div>
                        )}
                      </div>
                    </details>
                  )}
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
};

export default CreativeHistory;
